<template>
    <div class="reporting">
        <p>Pode efetuar extrações diárias e mensais manualmente utilizando o widget abaixo.</p>

        <widget-options>
            <widget-button
                v-if="$store.getters.can( 'reporting', 'dailyReporting' )"
                class="capitalize"
                :settings="{
                    action: () => toggleModal( 'dailyReporting' ),
                    label: 'dailyReporting'
                }"
            />
            <widget-button
                v-if="$store.getters.can( 'reporting', 'monthlyReporting' )"
                class="capitalize"
                :settings="{
                    action: () => toggleModal( 'monthlyReporting' ),
                    label: 'monthlyReporting'
                }"
            />
        </widget-options>
        <modal
            v-if="modal.show"
            :form="{
                class: 'modal__form--action display--flex',
                settings: {
                    submissionType: modal[modal.type].action
                }
            }"
            :customSettings="{
                color: 'green',
                button: {
                    label: 'export'
                }
            }"
            :executeCloseAction="!modal.loading"
            @close="toggleModal( modal.type )"
            :key="modal.show"
        >
            <template #modal__header >
                <h2 class="modal__header">{{ $t('global.exportType', { type: $t(`global.${modal.type}`) } ) }}</h2>
            </template>
            <template #modal__body >
                <base-field
                    v-if="modal.type == 'dailyReporting'"
                    class="reporting__modal__body__date"
                    :settings="{
                        label: { class: 'capitalize', text: $t('global.date') },
                        input: {
                            component: 'InputCalendar',
                            name: 'date',
                            id: 'date',
                            targetClass: 'field__input--default typography--default color--default',
                            value: '',
                            dynamicData: {
                                configs: {
                                    format: 'DD-MM-YYYY',
                                    hideInput: false,
                                    maxDate: new Date(),
                                }
                            },
                            browserAutocomplete: 'off',
                            isRequired: false,
                            isMissing: errors.missing.date,
                            errorOnSubmit: {
                                show: errors.api.date ? true : false,
                                messages: errors.api.date
                            },
                        },
                        reset,
                        allowFilling: true
                    }"
                />
                <base-field
                    v-if="modal.type == 'monthlyReporting'"
                    style="margin-right:20px;"
                    class="search__calendar"
                    :settings="{
                        label: { class: 'capitalize', text: $t('global.date') },
                        input: {
                            component: 'InputRangeCalendar',
                            name: 'reportingRange',
                            id: 'reportingRange',
                            targetClass: 'field__input--default typography--default color--default',
                            value: '',
                            browserAutocomplete: 'off',
                            noListener: true,
                            dynamicData: {
                                configs: {
                                    hideInput: false,
                                    maxDate: new Date(),
                                }
                            },
                            isRequired: false,
                            isMissing: errors.missing.fromDate,
                            errorOnSubmit: {
                                show: errors.api.fromDate ? true : false,
                                messages: errors.api.fromDate
                            }
                        },
                        reset,
                        allowFilling: true
                    }"
                />
                <base-field
                    v-if="modal.type == 'monthlyReporting'"
                    class="reporting__campaigns"
                    :settings="{
                        label: { class: 'capitalize', text: $t('global.campaign') },
                        input: {
                            component: 'InputAutocomplete',
                            name: 'campaignName',
                            id: 'campaignName',
                            type: 'text',
                            value: '',
                            targetClass: 'reporting__campaigns__input field__input--default typography--default color--default',
                            browserAutocomplete: 'off',
                            dynamicData: {
                                data: campaigns,
                            },
                            noListener: true
                        },
                        allowFilling: true
                    }"
                />
            </template>
            <template #modal__info >
                <base-button
                    class="modal__content__button--negative background--transparent border--grey"
                    :class="{ 'disable': modal.loading }"
                    @click="toggleModal( modal.type )"
                    v-html="$t('global.cancel')"
                />
            </template>
        </modal>
    </div>
</template>

<script>
// package
import {
    reactive,
    watch,
    toRefs
} from '@vue/composition-api';

// store
import { mapState, storePayload, useActions } from '@/hooks/use-store';

// hooks
import { portalComponents } from '@/hooks/use-data';

// helpers
import { get } from '@/helpers/requests';
import { sortObjectsArray } from '@/helpers/mixinGeneral';

export default {
    components: {
        ...portalComponents,
        WidgetButton: () => import( /* webpackChunkName: 'WidgetButton' */ '@/components/bundles/layout/widget/options/types/WidgetButton' ),
        Modal: () => import( /* webpackChunkName: 'Modal' */ '@/components/bundles/layout/modals/Modal' ),
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField' */ '@/components/bundles/fields/BaseField' )
    },
    setup () {
        const { errors, reset } = storePayload();

        // modal
        const modal = reactive({
            show: false,
            loading: mapState({ module: 'storePayload', key: 'loading' }),
            type: 'dailyReporting',
            dailyReporting: {
                field: {
                    name: 'endpoint',
                    value: '/reporting/extraction/users'
                },
                action: 'processExport'
            },
            monthlyReporting: {
                field: {
                    name: 'endpoint',
                    value: '/reporting/extraction/campaigns'
                },
                action: 'processExport'
            }
        });

        const { actions } = useActions();

        /**
         * [method]
         * Toggle da modal proveninente do widget.
         *
         * @since 1.1.2
         * @category Method
         * @autor Jorrge Monteiro
         *
        */
        const toggleModal = type => {
            // obtenção do campo para ser enviado para a store.
            const { field } = modal[type];

            modal.type = type;
            modal.show = !modal.show;

            // bloqueia a barra de adição / edição
            actions( 'controlActionBar', { value: modal.show });

            // Caso acção seja abrir a modal, e conforme
            // o tipo da modal, é adicionado a informação ao payload.
            // Caso acção seja fechar a modal, é removido todo o payload da store.
            ( modal.show )
                ? actions( 'registerField', field )
                : actions( 'resetState', false );
        };

        // obtenção do trigger
        const trigger = mapState({ module: 'storePayload', key: 'trigger' });

        // validação de quando o trigger é activo
        // de forma a fechar a modal.
        watch( () => trigger.value,
            newTrigger => {
                if ( newTrigger )
                    modal.show = false;
            });

        // lista de campanhas
        const requestData = reactive({
            campaigns: []
        });

        watch( () => modal.type,
            newType => {
                if ( newType === 'monthlyReporting' && modal.show )
                    getCampaignsRequest();
            });

        const getCampaignsRequest = ( async () => {
            requestData.campaigns = await get( '/campaigns' )
                .then( ({ data }) => data.content.data );
            requestData.campaigns = requestData.campaigns.map( key => key.name ).map( name => ({ value: name, label: name }) );
            // ordenar drop dos das campanhas
            sortObjectsArray( requestData.campaigns );
        });

        return {
            toggleModal,
            modal,
            errors,
            reset,
            ...toRefs( requestData )
        };
    }
};
</script>